import { findGgufVariants } from "../gguf/variants.js";
import { isGgufRepo } from "./choose-component.js";
import type { ResolvedRepo } from "./hub.js";

/**
 * The component as a *person* would name it, for the tab title.
 *
 * A safetensors component is a directory (`vae/`) and reads fine as-is. A GGUF
 * component is the variant's key, which is a whole filename — so the title came
 * out `ggml-org/embeddinggemma-300M-GGUF/embeddinggemma-300M-Q8_0.gguf`, which
 * repeats the model's name twice and is precisely the dead weight
 * `document-title.ts` exists to keep out of a truncated tab. Seen on production
 * during the first live pass, 2026-08-09.
 *
 * The variant already computes a short label — `Q8_0`, `BF16/…` — by stripping
 * the stem every file in the repo shares. Using it here means one name for a
 * variant in the picker, the tab and the URL's meaning, differing only in that
 * the URL carries the key because a key is unambiguous and a label need not be.
 */
/*
 * **Returns `undefined` for a repo with one model, not `""`.** The root
 * component's prefix *is* the empty string, and `return component` handed that
 * back verbatim — a value that is falsy, so it read as "no component" wherever
 * anyone tested it for truth, and is not `undefined`, so it survived every
 * `=== undefined` check.
 *
 * That reached production. `prepareShareCard` omits the part on strict
 * `undefined`, so every ordinary safetensors repo asked for its card with
 * `part: ""`, `/prepare` answered `400 {"status":"invalid"}`, and the ask is
 * fire-and-forget by design — so the browser swallowed our own service's
 * rejection and no safetensors card was generated for about three hours. The
 * only reason GGUF kept working is that a variant always has a real prefix.
 *
 * `writeUrlState` survived it by accident: it ends in `|| undefined`, which
 * launders the empty string on the way to the URL. One caller guarding itself is
 * not the same as the function being right.
 */
/*
 * **One canonical string, because three consumers hash or display it and two of
 * them must agree exactly.**
 *
 * A safetensors component's prefix is a directory *with its trailing slash* —
 * `vae/` — and this returned it verbatim. `writeUrlState` then stripped that
 * slash on the way to `?part=`, while `prepareShareCard` sent it intact. So the
 * card was stored under `partSegment("vae/")` = `aa879ad94d93` and the responder
 * advertised `partSegment("vae")` = `e089e84942af`, a key nothing ever wrote.
 * Every multi-component safetensors repo therefore produced a card that could
 * not be served, and because `og.tensorlens.dev` answers a miss with the
 * fallback PNG at status 200, it looked exactly like a working card.
 *
 * Found 2026-08-11 while writing the browser↔Lambda contract test, and it is the
 * same defect as the GGUF label/key confusion the day before: one identity, two
 * spellings, and the mismatch invisible because both sides render.
 *
 * `findComponents` already distinguishes them — `{prefix: "vae/", label: "vae"}`
 * — so the fix is to return the label rather than a de-slashed prefix. The
 * `replace(/\/$/, "")` in `writeUrlState` is now redundant and kept only as
 * belt-and-braces; it is not what makes this correct.
 */
export function componentLabel(
  repo: ResolvedRepo,
  component: string | undefined,
): string | undefined {
  if (!component) return undefined;
  if (!isGgufRepo(repo)) return component.replace(/\/$/, "");
  return (
    findGgufVariants(repo.files).find((v) => v.key === component)?.label ?? component
  );
}

/**
 * The part a submit should re-open, when it names the model already on screen.
 *
 * The loader bar has no field for the part: it is chosen from the picker and
 * then lives only in `state` and in `?part=`. So pressing Explore sent `load()`
 * a repo id and nothing else, and a repo holding several models answered the
 * only way it can — by showing the picker again.
 *
 * On a single-model repo that is invisible. On a GGUF it meant **compare could
 * not be reached at all**: Explore is what runs the comparison, and it dropped A
 * back to "pick one of 25", which clears `state.inventory`, which is the exact
 * condition the compare call is guarded on. B was resolved, typed into the form,
 * and silently never read. Nothing errored — the visitor just got a picker where
 * they expected a diff.
 *
 * Returns the **label**, so this path and a shared `?part=` link resolve through
 * `chooseComponent` identically. Anything else would be the third instance of
 * the one-identity-two-spellings bug this file already documents twice.
 *
 * @param submittedId the id as typed, already normalized by the caller
 */
export function partToReopen(
  submittedId: string,
  loaded:
    | { readonly repo: ResolvedRepo; readonly component: string | undefined }
    | undefined,
): string | undefined {
  /*
   * Only when the id is unchanged. Editing the field to a different model must
   * not carry a part across — `Q4_K_M` names nothing in another repo, and the
   * pinned revision is cleared on the same edit for the same reason.
   */
  if (!loaded || submittedId !== loaded.repo.repoId) return undefined;
  return componentLabel(loaded.repo, loaded.component);
}
